import React, { forwardRef, Ref } from "react";

import * as Sentry from "@sentry/react";
import { Provider } from "react-redux";
import {
    BrowserRouter,
    Route,
    Routes,
    Link,
    LinkProps
} from "react-router-dom";

import AppProvider, {
    type RouterLinkComponentProps
} from "@atlaskit/app-provider";

import {
    FastshiftDashboard,
    FastshiftResourcesPage,
    FastshiftMigrationsManagementPage,
    FastshiftMigrationTicketsPage,
    PageLayout,
    FastshiftPage
} from "@/components/fastshift/src";
import { FourOhFourDisplay } from "@/components/fastshift/src/common/displays";
import { GenericErrorDisplay } from "@/components/fastshift/src/common/displays";
import {
    FASTSHIFT_PATHS,
    FASTSHIFT_PATH_KEYS
} from "@/components/fastshift/src/common/routes/constants";
import { beforeCaptureWithScope } from "@/components/fastshift/src/common/utils/sentry-with-scope";
import Flag from "@/components/fastshift/src/contexts/FlagProvider";
import { FastshiftCustomerSelectionPage } from "@/components/fastshift/src/pages/customer-selection";
import { store as FastShiftPortalStore } from "@/components/fastshift/src/state/store";
import { ClientOnly } from "@/utils/client-only";

import { RedirectToSelectionPage } from "./redirect-to-selection-page";

const CustomRouterLink = forwardRef(
    (
        { href, children, ...rest }: RouterLinkComponentProps<LinkProps>,
        ref: Ref<HTMLAnchorElement>
    ) => {
        // A basic link by passing a string as the component's `href` prop.
        if (typeof href === "string") {
            return (
                <Link ref={ref} to={href} {...rest}>
                    {children}
                </Link>
            );
        }

        // Advanced link configuration by passing an object as the
        // component's `href` prop
        return (
            <Link
                ref={ref}
                // href={href.href}
                to={href.to}
                replace={href.replace}
                {...rest}
            >
                {children}
            </Link>
        );
    }
);

export const FastshiftPortalPageRouter: React.FC = () => {
    return (
        <ClientOnly>
            <Sentry.ErrorBoundary
                beforeCapture={beforeCaptureWithScope("catch-all")}
                fallback={<GenericErrorDisplay />}
            >
                <AppProvider routerLinkComponent={CustomRouterLink}>
                    <BrowserRouter>
                        <Flag.Provider>
                            <Provider store={FastShiftPortalStore}>
                                <style
                                    dangerouslySetInnerHTML={{
                                        __html: `.grecaptcha-badge { visibility: hidden !important; }`
                                    }}
                                />

                                <Routes>
                                    {/* Layout route keeps PageLayout mounted for all fastshift routes */}
                                    <Route path="/:locale?">
                                        <Route element={<PageLayout />}>
                                            {/* Selection */}
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .SELECTION
                                                    ]
                                                }
                                                element={
                                                    <FastshiftCustomerSelectionPage />
                                                }
                                            />
                                            {/* Domain-scoped pages */}
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .OVERVIEW
                                                    ]
                                                }
                                                element={<FastshiftDashboard />}
                                            />
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .TICKETS
                                                    ]
                                                }
                                                element={
                                                    <FastshiftMigrationTicketsPage />
                                                }
                                            />
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .PLANNING
                                                    ]
                                                }
                                                element={
                                                    <FastshiftMigrationsManagementPage />
                                                }
                                            />
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .RESOURCES_DOMAIN_SCOPED
                                                    ]
                                                }
                                                element={
                                                    <FastshiftResourcesPage />
                                                }
                                            />
                                            {/* Partner-scoped resources without domain */}
                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .RESOURCES_PARTNER_SCOPED
                                                    ]
                                                }
                                                element={
                                                    <FastshiftResourcesPage />
                                                }
                                            />

                                            <Route
                                                path={
                                                    FASTSHIFT_PATHS[
                                                        FASTSHIFT_PATH_KEYS
                                                            .FASTSHIFT
                                                    ]
                                                }
                                                element={<FastshiftPage />}
                                            />

                                            {/* Legacy redirect */}
                                            <Route
                                                path="fastshift/dashboard"
                                                element={
                                                    <RedirectToSelectionPage />
                                                }
                                            />

                                            {/* Catch all */}
                                            <Route
                                                path="fastshift/*"
                                                element={<FourOhFourDisplay />}
                                            />
                                        </Route>
                                    </Route>
                                </Routes>
                            </Provider>
                        </Flag.Provider>
                    </BrowserRouter>
                </AppProvider>
            </Sentry.ErrorBoundary>
        </ClientOnly>
    );
};
